import { siteConfig } from '@sighthost/config';

/**
 * payLinkMessage — paste-ready payment-request messages built around a
 * `/pay/:id?token=…` link.
 *
 * Why a message and not just a URL: staff paste these into WhatsApp, which is
 * how most SA SMEs transact. A bare link from an unfamiliar number, asking for
 * card details, reads as phishing. Three properties defuse that and are
 * non-negotiable in every variant below:
 *   1. the document reference and exact amount, so the customer can verify it
 *      against something they already hold;
 *   2. an explicit statement that the link dies once paid (true — see
 *      payfast-public-initiate, which 409s on a settled invoice);
 *   3. an identifiable sender and a support address to reply to.
 *
 * Tone is formal business-to-business per owner direction 2026-07-27.
 *
 * PLAIN ASCII ONLY. No em-dashes, no smart quotes, no non-breaking spaces.
 * An em-dash in a PayFast `item_name` broke signature validation on
 * 2026-07-05 (commit acb903e); the habit is worth keeping across all
 * customer-facing generated copy, and WhatsApp/SMS gateways mangle some
 * Unicode punctuation anyway.
 *
 * Mirrors the existing clipboard-block pattern in
 * apps/sales/lib/introEmail.ts. Spec: PAY_LINK_DISTRIBUTION_IMPLEMENTATION_PLAN_2026-07-27 §4.2
 */

export interface PayLinkMessageInput {
  /** Full absolute URL, including the ?token= query. */
  payUrl: string;
  /** QUO-2026-000001 or INV-2026-000044. */
  reference: string;
  docLabel: 'quotation' | 'invoice';
  amountZar: number;
  /** Registered/contact name. Omitted from the greeting when absent. */
  customerName?: string | null;
  /** Staff member's display name. When absent the message signs off as the company. */
  senderName?: string | null;
  /** Already-formatted date, e.g. "31 July 2026". */
  dueLabel?: string | null;
  /**
   * True when payUrl carries &autopay=1 — settles this invoice AND enrols
   * PayFast Recurring. The customer must be told that second part explicitly;
   * enrolling someone in a recurring mandate without saying so is not on.
   */
  autopay?: boolean;
}

const PAYMENT_METHODS =
  'card, Instant EFT, Capitec Pay, SnapScan, Zapper and Mobicred';

function amount(zar: number): string {
  return new Intl.NumberFormat('en-ZA', {
    style: 'currency',
    currency: 'ZAR',
    minimumFractionDigits: 2,
  })
    .format(zar)
    // Intl emits U+00A0 (non-breaking space) between symbol and digits on some
    // runtimes. Written as an escape rather than a literal so the intent survives
    // diff review; WhatsApp and SMS gateways mangle U+00A0.
    .replace(/\u00A0/g, ' ');
}

/** Drop empty sections without leaving blank-line runs. */
function joinLines(lines: Array<string | null | undefined>): string {
  return lines.filter((l) => l !== null && l !== undefined).join('\n');
}

function greeting(customerName?: string | null): string {
  const name = customerName?.trim();
  return name ? `Good day ${name},` : 'Good day,';
}

function signOff(senderName?: string | null): string[] {
  const name = senderName?.trim();
  return [
    '',
    'Kind regards',
    ...(name ? [name] : []),
    siteConfig.legalName,
    siteConfig.supportEmail,
  ];
}

function validityLine(input: PayLinkMessageInput): string | null {
  if (!input.dueLabel) return null;
  return input.docLabel === 'quotation'
    ? `Valid until: ${input.dueLabel}`
    : `Due date: ${input.dueLabel}`;
}

/**
 * The two sentences that carry the trust load. Kept together so the WhatsApp
 * and email variants can never drift apart on the security claim.
 */
function assurance(autopay: boolean): string {
  const base =
    `Accepted payment methods: ${PAYMENT_METHODS}. ` +
    'This link is unique to the reference above and becomes invalid once payment has been received.';
  if (!autopay) return base;
  return (
    `${base} Completing this payment will also activate automatic collection of future ` +
    'invoices from the same card. Automatic collection can be cancelled at any time from ' +
    'the client portal.'
  );
}

/** WhatsApp / SMS variant. No markdown - WhatsApp renders asterisks as bold. */
export function buildWhatsAppMessage(input: PayLinkMessageInput): string {
  const noun = input.docLabel === 'quotation' ? 'quotation' : 'invoice';
  return joinLines([
    `${siteConfig.legalName} - Payment request`,
    '',
    greeting(input.customerName),
    '',
    `Please find below the secure payment link for ${noun} ${input.reference}.`,
    '',
    `Amount due: ${amount(input.amountZar)}`,
    `Reference: ${input.reference}`,
    validityLine(input),
    '',
    input.payUrl,
    '',
    assurance(!!input.autopay),
    '',
    'Should you require anything further, please contact us at the address below.',
    ...signOff(input.senderName),
  ]);
}

/** Email subject line. Kept short so it survives mobile truncation. */
export function buildEmailSubject(input: PayLinkMessageInput): string {
  const noun = input.docLabel === 'quotation' ? 'Quotation' : 'Invoice';
  return `${noun} ${input.reference} - ${amount(input.amountZar)} - payment link`;
}

/** Email body. Plain text so it pastes cleanly into any client. */
export function buildEmailBody(input: PayLinkMessageInput): string {
  const noun = input.docLabel === 'quotation' ? 'quotation' : 'invoice';
  return joinLines([
    greeting(input.customerName),
    '',
    `Please find below the secure payment link for ${noun} ${input.reference}.`,
    '',
    `Amount due: ${amount(input.amountZar)}`,
    `Reference: ${input.reference}`,
    validityLine(input),
    '',
    input.payUrl,
    '',
    assurance(!!input.autopay),
    ...signOff(input.senderName),
  ]);
}

/** Subject + body as one clipboard block, matching introEmail.ts's shape. */
export function buildEmailBlock(input: PayLinkMessageInput): string {
  return joinLines([
    `Subject: ${buildEmailSubject(input)}`,
    '',
    buildEmailBody(input),
  ]);
}

/**
 * Absolute pay URL. Callers hold invoice_id + payment_token; this is the one
 * place the shape of the link is written down, so a future change to the route
 * does not need hunting across six call sites.
 */
export function buildPayUrl(
  invoiceId: string,
  paymentToken: string,
  opts?: { autopay?: boolean }
): string {
  const base = `${siteConfig.portal.productionUrl}/pay/${invoiceId}?token=${paymentToken}`;
  return opts?.autopay ? `${base}&autopay=1` : base;
}
